import { computed, inject, Injectable, PLATFORM_ID, Signal, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { toSignal } from '@angular/core/rxjs-interop';
import { Auth, authState, isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink, user, User } from '@angular/fire/auth';
import { collection, collectionData, doc, docData, Firestore } from '@angular/fire/firestore';
import { setDoc } from 'firebase/firestore';
import { lastValueFrom, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { RequestService } from './request.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user: Signal<User | null>;
  data = signal(null);
  contactData = computed(() => {
    const data = this.data();
    return data?.contactData;
  });
  private readonly auth = inject(Auth);
  private readonly authState = authState(this.auth);
  private readonly platformId = inject(PLATFORM_ID);
  private loginTracked = false;

  constructor(
    private firestore: Firestore,
    private request: RequestService
  ) {
    this.user = toSignal(user(this.auth), { initialValue: null });
    this.authState.subscribe(async (user) => {
      if (user) {
        this.loadFirestore();
        this.request.setAccessToken(await user.getIdToken());
        // Login-Ereignis einmal pro App-Sitzung, nur im Browser (n8n dedupliziert
        // zusätzlich auf ein Ereignis pro Nutzer und Tag).
        if (isPlatformBrowser(this.platformId) && !this.loginTracked) {
          this.loginTracked = true;
          this.request.trackLogin();
        }
      } else {
        this.data.set(null);
        this.loginTracked = false;
      }
    });
  }

  getInvoices(): Observable<any[]> {
    return user(this.auth).pipe(
      switchMap((user) => {
        if (user) {
          return this.request.getMyInvoices();
          return collectionData(
            collection(this.firestore, `/users/${user.uid}/invoices`)
          );
        } else {
          return of([]);
        }
      })
    );
  }

  getInvoice(id: string): Observable<any> {
    return user(this.auth).pipe(
      switchMap((user) => {
        if (user) {
          return this.request.getInvoiceById(id);
        } else {
          return of(null);
        }
      })
    );
  }

  async signInWithEmail() {
    if (await isSignInWithEmailLink(this.auth, window.location.href)) {
      try {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        await signInWithEmailLink(this.auth, email, window.location.href);
      } catch (error) {
        console.error('Error signing in with email and password:', error);
        throw error;
      }
    }
  }

  async sendSignInLink(email: string) {
    const actionCodeSettings = {
      url: window.location.href,
      handleCodeInApp: true,
    };

    sendSignInLinkToEmail(this.auth, email, actionCodeSettings)
      .then(() => {
        window.localStorage.setItem('emailForSignIn', email);
        return true;
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        return false;
      });
  }

  async signOut() {
    try {
      await this.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  async isEmailVerified(): Promise<boolean> {
    const user = await this.auth.currentUser;
    if (user) {
      await user.reload();
      return user.emailVerified;
    }
    return false;
  }

  async login() {
    const email = await window.prompt(
      'Please provide your email for confirmation'
    );
    if (email) {
      return await this.sendSignInLink(email);
    }
  }

  async logout() {
    return await this.signOut();
  }

  private async loadFirestore() {
    const user = this.user();
    if (!user || !user.uid) return null;
    try {
      const userDoc = await lastValueFrom(
        docData(doc(this.firestore, '/users/' + user.uid)).pipe(take(1))
      );
      if (userDoc) {
        this.data.set(userDoc);
        return userDoc;
      }
      return null;
    } catch (error) {
      console.error('Error loading user data:', error);
      return null;
    }
  }

  async saveData(newData: any) {
    const user = this.user();
    if (!user || !user.uid) return null;
    const currentData = this.data();
    const dataToSave = { ...currentData, ...newData };
    try {
      await setDoc(doc(this.firestore, '/users/' + user.uid), dataToSave);
      this.data.set(dataToSave);
      return true;
    } catch (error) {
      console.error('Error writing user data:', error);
      throw error;
    }
  }
}
